import { z } from 'zod';

/**
 * A classic mini-league a manager belongs to — enough to list and to link
 * into its standings page. FPL also has "h2h" leagues, which this app
 * doesn't surface: mini-league standings are the classic table everyone
 * actually competes over.
 */
export const LeagueSummary = z.object({
  id: z.number().int().positive(),
  name: z.string(),
});
export type LeagueSummary = z.infer<typeof LeagueSummary>;

/** One manager's row in a league's standings table. */
export const LeagueStandingsEntry = z.object({
  rank: z.number().int(),
  /** Rank as of the previous gameweek; null before a league has any history. */
  previousRank: z.number().int().nullable(),
  entryId: z.number().int().positive(),
  teamName: z.string(),
  managerName: z.string(),
  gameweekPoints: z.number(),
  totalPoints: z.number(),
});
export type LeagueStandingsEntry = z.infer<typeof LeagueStandingsEntry>;

/** One page of a classic league's standings, from
 * `leagues-classic/{id}/standings/`. Never cached — see `getLeagueStandings`. */
export const LeagueStandingsPage = z.object({
  leagueId: z.number().int().positive(),
  name: z.string(),
  page: z.number().int().positive(),
  hasNext: z.boolean(),
  entries: z.array(LeagueStandingsEntry),
});
export type LeagueStandingsPage = z.infer<typeof LeagueStandingsPage>;

/**
 * How many of a mini-league's rivals hold or captained the single player
 * that leads the league on that count — "most-owned" and "most-captained"
 * are each this shape. `rivalsRead` is the honest denominator: how many
 * rivals were actually read, not the league's size, the same contract
 * `EffectiveOwnership` documents.
 */
export const LeagueInsightPlayerStat = z.object({
  fplId: z.number().int(),
  name: z.string(),
  count: z.number().int().min(0),
  rivalsRead: z.number().int().min(0),
  percent: z.number().min(0),
});
export type LeagueInsightPlayerStat = z.infer<typeof LeagueInsightPlayerStat>;

/**
 * The league's biggest differential: among players a small share of the
 * rivals read hold, the one with the best form — the pick most likely to
 * swing the table, since so few others have him.
 */
export const LeagueDifferentialPick = z.object({
  fplId: z.number().int(),
  name: z.string(),
  ownedBy: z.number().int().min(0),
  rivalsRead: z.number().int().min(0),
  ownedPercent: z.number().min(0),
  form: z.number(),
});
export type LeagueDifferentialPick = z.infer<typeof LeagueDifferentialPick>;

/**
 * One manager's template score: how many of their picks belong to the
 * league's consensus (a player more than half the rivals read hold), out of
 * their squad size. `isYou` flags the signed-in manager's own row, so the UI
 * never has to search for it.
 */
export const LeagueTemplateScore = z.object({
  entryId: z.number().int().positive(),
  teamName: z.string(),
  managerName: z.string(),
  isYou: z.boolean(),
  matchCount: z.number().int().min(0),
  squadSize: z.number().int().min(0),
  rate: z.number().min(0).max(1),
});
export type LeagueTemplateScore = z.infer<typeof LeagueTemplateScore>;

/**
 * A mini-league's rival picks read a second way: not just who owns what, but
 * what it means — the template, the biggest differential, and every read
 * manager's distance from consensus. `rivalsRead`/`rivalCap` are the same
 * honest-denominator contract `EffectiveOwnership` documents, deliberately
 * excluding the signed-in manager's own picks from the rival pool that every
 * count and rate above is computed against — comparing a manager to a pool
 * that includes themselves isn't a comparison. A league with nobody else in
 * it, or where every rival fetch failed, reports `rivalsRead: 0` and
 * `null` findings rather than a fabricated zero or a crash.
 */
export const LeagueInsights = z.object({
  rivalsRead: z.number().int().min(0),
  rivalCap: z.number().int().positive(),
  mostOwned: LeagueInsightPlayerStat.nullable(),
  mostCaptained: LeagueInsightPlayerStat.nullable(),
  differential: LeagueDifferentialPick.nullable(),
  templateScores: z.array(LeagueTemplateScore),
  /** The signed-in manager's own FPL entry id, or null when none of their
   * squads carry one — the same identity `templateScores[].isYou` is keyed
   * against. */
  yourEntryId: z.number().int().positive().nullable(),
});
export type LeagueInsights = z.infer<typeof LeagueInsights>;
